import React from "react";
import { Platform, View } from "react-native";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { SEO } from "components/Seo/Seo";
import { AccountLayout } from "components/AccountLayout/AccountLayout";
import { useQuery } from "react-query";
import { instance } from "@clearabee/ui-sdk";
import { JobsView } from "components/JobsView/JobsView";
import { BookServicePill } from "components/BookServicePill/BookServicePill";
import { AccountFAQs } from "components/AccountFAQs/AccountFAQs";
import { useData } from "lib/hooks/useData";
import { UpcomingJobPill } from "containers/UpcomingJobPill/UpcomingJobPill";
import { Panel, theme } from "@clearabee/ui-library-native";
import { SkeletonLoader } from "components/SkeletonLoader/SkeletonLoader";
import { Rect } from "react-native-svg";
import { PreviousJobPill } from "containers/PreviousJobPill/PreviousJobPill";
import { DashboardInfo } from "components/DashboardInfo/DashboardInfo";
import { User } from "lib/auth";
import { getEnv } from "lib/getEnv";
import { ButtonLink } from "components/Core/ButtonLink/ButtonLink";
import { styles, ids } from "./UserDashboard.styles";

export const numberOfCompletedJobs = 3;

interface UserDashboardProps {
  user: User;
}

export const UserDashboard = ({
  user,
}: UserDashboardProps): React.ReactElement | null => {
  const [translate] = useTranslation();
  const [{ serverDate }] = useData();

  const { data: activeJobsData, isLoading: isActiveJobLoading } = useQuery(
    ["getActiveJobs", user?.email],
    async () => {
      return (
        await instance.jobs.postQueryJobs({
          limit: 1,
          orderBy: { date: "asc" },
          filters: [
            ["date", ">=", dayjs.utc(serverDate).startOf("day")],
            ["status", "!=", "Completed"],
            ["status", "!=", "Failed"],
          ],
        })
      ).data;
    },
    {
      enabled: !!user,
    },
  );

  const { data: completedJobs, isLoading: isCompletedJobsLoading } = useQuery(
    ["getCompletedJobs", user?.email],
    async () => {
      return (
        await instance.jobs.postQueryJobs({
          limit: numberOfCompletedJobs,
          orderBy: { date: "desc" },
          filters: [["status", "=", "Completed"]],
        })
      ).data.items;
    },
    {
      enabled: !!user,
    },
  );

  if (!user) {
    return null;
  }

  const activeJob = activeJobsData?.items[0];

  return (
    <>
      <SEO title={translate("hwa.screens.dashboard.title")} />

      <AccountLayout
        title={
          Platform.OS !== "web"
            ? `${translate("common.hey")} ${user?.firstName}!`
            : translate("hwa.screens.dashboard.title")
        }
        subtitle={translate("common.welcomeBack")}
        tab="Dashboard"
        user={user}
      >
        {(isActiveJobLoading ||
          (!activeJob && !isActiveJobLoading && isCompletedJobsLoading)) && (
          <Panel
            styles={styles.panel}
            dataSet={{ media: ids.panel }}
            shadow={false}
          >
            <SkeletonLoader
              speed={1}
              backgroundColor={theme.colors.light.base}
              foregroundColor={`${theme.colors.greyscale.lighter}00`}
              height={108}
              width="100%"
            >
              <Rect x="0" y="0" rx="8" ry="8" width="100%" height="100%" />
            </SkeletonLoader>
          </Panel>
        )}

        {activeJob && !isActiveJobLoading && (
          <UpcomingJobPill job={activeJob} />
        )}

        {!activeJob &&
          !isActiveJobLoading &&
          !isCompletedJobsLoading &&
          !!completedJobs?.length && <PreviousJobPill job={completedJobs[0]} />}

        <View
          style={styles.BookServicePillContainer}
          dataSet={{ media: ids.BookServicePillContainer }}
        >
          <BookServicePill />
        </View>
        <JobsView skeletonSize={numberOfCompletedJobs} jobs={completedJobs} />
        <View
          style={styles.orderTrackingButtonContainer}
          dataSet={{ media: ids.orderTrackingButtonContainer }}
        >
          <ButtonLink
            routeName="Redirect"
            params={{
              url: `${getEnv("WEBSITE_URL")}/order-tracking`,
            }}
            color="greyscale"
            size="medium"
            newTab
          >
            {translate("common.orderTracking")}
          </ButtonLink>
        </View>
        <AccountFAQs />
        <DashboardInfo />
      </AccountLayout>
    </>
  );
};
